/* eslint-disable no-console */
import { useCallback, useEffect, useRef } from "react";
import { ApolloClient, useApolloClient } from "@apollo/client";
import type { UseToastOptions } from "@chakra-ui/react";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import useLatestRef from "utils/useLatestRef";
import usePrevious from "utils/usePrevious";

export type PollFetchContext = {
  client: ApolloClient<object>;
};

type UsePollForIdsOptions = {
  ids: UUID[] | null | undefined;
  errorToastOptions?: UseToastOptions;
  maxTries?: number | null;
  performPollFetch: (ids: UUID[], context: PollFetchContext) => Promise<void>;
  pollInterval?: number;
};

export default function usePollForIds({
  ids,
  errorToastOptions,
  maxTries = 20,
  performPollFetch,
  pollInterval = 10e3,
}: UsePollForIdsOptions) {
  const latestIds = useLatestRef(ids);
  const latestPerformPollFetch = useLatestRef(performPollFetch);
  const latestErrorToastOptions = useLatestRef(errorToastOptions);

  const client = useApolloClient();

  const previousIds = usePrevious(ids);

  const { showErrorToast } = useAsyncCompleteToast();

  const performFetch = useCallback(
    (idsInFetch: UUID[]) => {
      latestPerformPollFetch
        .current(idsInFetch, { client })
        .catch((reason) =>
          showErrorToast(reason, latestErrorToastOptions.current),
        );
    },
    [client, latestPerformPollFetch, latestErrorToastOptions, showErrorToast],
  );

  const intervalIdRef = useRef<NodeJS.Timer>();
  const numTriesRef = useRef(0);

  const endPolling = useCallback(() => {
    console.debug("endPolling called");

    if (intervalIdRef.current) {
      console.debug("ending polling");
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = undefined;
      numTriesRef.current = 0;
    }
  }, []);

  const startPolling = useCallback(() => {
    console.debug("startPolling called");

    if (intervalIdRef.current !== undefined) {
      endPolling();
    }

    console.debug("starting polling");
    intervalIdRef.current = setInterval(() => {
      const idsToPoll = latestIds.current;

      if (maxTries && numTriesRef.current >= maxTries) {
        console.debug("exceeded max tries");
        endPolling();
      } else if (!idsToPoll?.length) {
        console.debug("no ids left to poll");
        endPolling();
      } else {
        performFetch(idsToPoll);
        numTriesRef.current += 1;
      }
    }, pollInterval);
  }, [endPolling, latestIds, maxTries, pollInterval, performFetch]);

  useEffect(() => {
    if (
      ids?.length &&
      (ids.some((id) => !previousIds?.includes(id)) || !intervalIdRef.current)
    ) {
      startPolling();
    } else if (!ids?.length) {
      endPolling();
    }
  }, [ids, previousIds, startPolling, endPolling]);

  useEffect(
    () => () => {
      console.debug("unmounting polling");
      endPolling();
    },
    [endPolling],
  );
}
